/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import { selectEmbedToken, selectTabList, selectTargetDate } from 'modules/dashboard/selectors';
import { AssistantIcon } from 'modules/common/components';
import {
  selectAdrIsDisplayPS,
  selectCxlCountIsDisplay,
  selectCxlIsDisplayPS,
  selectOccupiedRoomsIsDisplayPS,
  selectPaceCancellationFilterList,
  selectPaceCumulativeView,
  selectPaceLagAligned,
  selectPaceSegmentAbsoluteCumulativeVisuals,
  selectPaceSegmentLagAlignedCumulativeVisuals,
  selectPaceSegmentLagAlignedVisuals,
  selectPaceSegmentVisuals,
  selectPaceWidgetSegmentFilterList,
  selectRevenueIsDisplayPS,
} from '../../../../../pace-tabs/selectors';
import { paceActions } from '../../../../../pace-tabs/slice';
import { isEmpty } from '../../../../../../../../common/helpers/object';
import { EXPORT_OPTIONS } from '../../../../../../../constants';

/**
 * Pace segment widget report with out filters
 * @param {Boolean} setIsSegmentLoaded - Visual loading state
 * @returns
 */
const PaceSegmentGraphicalWidget = ({ setIsSegmentLoaded }) => {
  const dispatch = useDispatch();
  const tokenDetails = useSelector(selectEmbedToken);
  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const paceSegmentVisualFilterList = useSelector(selectPaceWidgetSegmentFilterList);
  const paceLagAligned = useSelector(selectPaceLagAligned);
  const paceCumulative = useSelector(selectPaceCumulativeView);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayPS);
  const occupiedRoomsIsDisplay = useSelector(selectOccupiedRoomsIsDisplayPS);
  const adrIsDisplay = useSelector(selectAdrIsDisplayPS);
  const cxlIsDisplay = useSelector(selectCxlIsDisplayPS);
  const cxlCountIsDisplay = useSelector(selectCxlCountIsDisplay);
  const cancellationFilterList = useSelector(selectPaceCancellationFilterList);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const paceSegmentVisuals = useSelector(selectPaceSegmentVisuals);
  const paceLagAlignedVisuals = useSelector(selectPaceSegmentLagAlignedVisuals);
  const paceSegmentAbsoluteCumulativeVisuals = useSelector(
    selectPaceSegmentAbsoluteCumulativeVisuals
  );
  const paceSegmentLagAlignedCumulativeVisuals = useSelector(
    selectPaceSegmentLagAlignedCumulativeVisuals
  );
  const targetDate = useSelector(selectTargetDate);
  //
  const [isPace1VisualsLoaded, setIsPace1VisualLoaded] = useState(false);
  const [isPace2VisualsLoaded, setIsPace2VisualLoaded] = useState(false);
  const [isPace3VisualsLoaded, setIsPace3VisualLoaded] = useState(false);
  const [isPace4VisualsLoaded, setIsPace4VisualLoaded] = useState(false);
  const [isPace5VisualsLoaded, setIsPace5VisualLoaded] = useState(false);
  //
  const [isPace1VisualsRendered, setIsPace1VisualRendered] = useState(false);
  const [isPace2VisualsRendered, setIsPace2VisualRendered] = useState(false);
  const [isPace3VisualsRendered, setIsPace3VisualRendered] = useState(false);
  const [isPace4VisualsRendered, setIsPace4VisualRendered] = useState(false);
  const [isPace5VisualsRendered, setIsPace5VisualRendered] = useState(false);
  const [isV1Processing, setIsV1Processing] = useState(false);
  const [isV2Processing, setIsV2Processing] = useState(false);
  const [isV3Processing, setIsV3Processing] = useState(false);
  const [isV4Processing, setIsV4Processing] = useState(false);
  const [isV5Processing, setIsV5Processing] = useState(false);
  //
  const [pace1Report, setPace1Report] = useState({});
  const [pace2Report, setPace2Report] = useState({});
  const [pace3Report, setPace3Report] = useState({});
  const [pace4Report, setPace4Report] = useState({});
  const [pace5Report, setPace5Report] = useState({});
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  useEffect(() => {
    // Calculate S based on the given conditions
    const pair1 = revenueIsDisplay ? revenueIsDisplay && isPace1VisualsLoaded : true;
    const pair2 = occupiedRoomsIsDisplay ? occupiedRoomsIsDisplay && isPace2VisualsLoaded : true;
    const pair3 = adrIsDisplay ? adrIsDisplay && isPace3VisualsLoaded : true;
    const pair4 = cxlIsDisplay ? cxlIsDisplay && isPace4VisualsLoaded : true;
    const pair5 = cxlCountIsDisplay ? cxlCountIsDisplay && isPace5VisualsLoaded : true;

    if (
      revenueIsDisplay &&
      isPace1VisualsLoaded &&
      occupiedRoomsIsDisplay &&
      isPace2VisualsLoaded &&
      adrIsDisplay &&
      isPace3VisualsLoaded &&
      cxlIsDisplay &&
      isPace4VisualsLoaded &&
      cxlCountIsDisplay &&
      isPace5VisualsLoaded
    ) {
      setIsSegmentLoaded(true);
    } else {
      setIsSegmentLoaded(pair1 || pair2 || pair3 || pair4 || pair5);
    }
  }, [
    revenueIsDisplay,
    isPace1VisualsLoaded,
    occupiedRoomsIsDisplay,
    isPace2VisualsLoaded,
    adrIsDisplay,
    isPace3VisualsLoaded,
    cxlIsDisplay,
    isPace4VisualsLoaded,
    cxlCountIsDisplay,
    isPace5VisualsLoaded,
  ]);
  //
  const getVisual = (index) => {
    let found;
    if (paceCumulative && !paceLagAligned) {
      found = paceSegmentAbsoluteCumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (paceCumulative && paceLagAligned) {
      found = paceSegmentLagAlignedCumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (paceLagAligned && !paceCumulative) {
      found = paceLagAlignedVisuals?.find((visual) => visual?.order === index);
    } else {
      found = paceSegmentVisuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = paceSegmentVisualFilterList
  ) => {
    const found = getVisual(index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(report) => setPBIReport(report)}
          setIsLoaded={(loading) => setIsLoaded(loading)}
          setIsRendered={(r) => setIsRendered(r)}
          tab={TAB_VALUE.PACE_TEST}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  //
  const getVisualName = (order) => {
    const found = getVisual(order);
    return found?.name;
  };
  //
  return (
    !isEmpty(paceSegmentVisualFilterList) && (
      <>
        <VisualGrid
          visualName={getVisualName(0)}
          isDisplay={revenueIsDisplay}
          visual={getWidget(
            0,
            styles.paceVisual,
            setPace1Report,
            setIsPace1VisualLoaded,
            setIsPace1VisualRendered,
            paceActions.setRevenueIsDisplayPS
          )}
          onClick={() => {
            dispatch(paceActions.setRevenueIsDisplayPS(!revenueIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(0)}
              report={pace1Report}
              isProcessing={isV1Processing}
              setIsProcessing={setIsV1Processing}
              disabled={!isPace1VisualsRendered || isV1Processing || !revenueIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
          information="Only the Top 10 segments (by value) applicable to the filter selection are displayed from the Pace Segment visuals"
        />
        <VisualGrid
          visualName={getVisualName(1)}
          isDisplay={occupiedRoomsIsDisplay}
          visual={getWidget(
            1,
            styles.paceVisual,
            setPace2Report,
            setIsPace2VisualLoaded,
            setIsPace2VisualRendered,
            paceActions.setOccupiedRoomIsDisplayPS
          )}
          onClick={() => {
            dispatch(paceActions.setOccupiedRoomIsDisplayPS(!occupiedRoomsIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              date={targetDate}
              visualName={getVisualName(1)}
              report={pace2Report}
              isProcessing={isV2Processing}
              setIsProcessing={setIsV2Processing}
              disabled={!isPace2VisualsRendered || isV2Processing || !occupiedRoomsIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
          information="Only the Top 10 segments (by value) applicable to the filter selection are displayed from the Pace Segment visuals"
        />
        <VisualGrid
          visualName={getVisualName(2)}
          isDisplay={adrIsDisplay}
          visual={getWidget(
            2,
            styles.paceVisual,
            setPace3Report,
            setIsPace3VisualLoaded,
            setIsPace3VisualRendered,
            paceActions.setAdrIsDisplayPS
          )}
          onClick={() => {
            dispatch(paceActions.setAdrIsDisplayPS(!adrIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              date={targetDate}
              visualName={getVisualName(2)}
              report={pace3Report}
              isProcessing={isV3Processing}
              setIsProcessing={setIsV3Processing}
              disabled={!isPace3VisualsRendered || isV3Processing || !adrIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
          information="Only the Top 10 segments (by value) applicable to the filter selection are displayed from the Pace Segment visuals"
        />
        <VisualGrid
          visualName={getVisualName(3)}
          isDisplay={cxlIsDisplay}
          visual={getWidget(
            3,
            styles.paceVisual,
            setPace4Report,
            setIsPace4VisualLoaded,
            setIsPace4VisualRendered,
            paceActions.setCxlIsDisplayPS
          )}
          onClick={() => {
            dispatch(paceActions.setCxlIsDisplayPS(!cxlIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              date={targetDate}
              visualName={getVisualName(3)}
              report={pace4Report}
              isProcessing={isV4Processing}
              setIsProcessing={setIsV4Processing}
              disabled={!isPace4VisualsRendered || isV4Processing || !cxlIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
          information="Only the Top 10 segments (by value) applicable to the filter selection are displayed from the Pace Segment visuals"
        />
        <VisualGrid
          visualName={getVisualName(4)}
          isDisplay={cxlCountIsDisplay}
          visual={getWidget(
            4,
            styles.paceVisual,
            setPace5Report,
            setIsPace5VisualLoaded,
            setIsPace5VisualRendered,
            paceActions.setCxlCountIsDisplay,
            cancellationFilterList
          )}
          onClick={() => {
            dispatch(paceActions.setCxlCountIsDisplay(!cxlCountIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              date={targetDate}
              visualName={getVisualName(4)}
              report={pace5Report}
              isProcessing={isV5Processing}
              setIsProcessing={setIsV5Processing}
              disabled={!isPace5VisualsRendered || isV5Processing || !cxlCountIsDisplay}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
          information="Only the Top 10 segments (by value) applicable to the filter selection are displayed from the Pace Segment visuals"
        />
      </>
    )
  );
};
//
export default PaceSegmentGraphicalWidget;
